// Microdata building blocks for Google Hotel Center price accuracy validation.
//
// The validator crawls the landing page (property page with dates), follows
// data-nav-criticalpath clicks, and on the page flagged data-nav-stage-final
// reads ONE offer: itemscope Hotel + LodgingReservation → makesOffer (Offer +
// LodgingReservation) → priceSpecification (CompoundPriceSpecification) whose
// `price` is the total stay cost with every tax and fee, broken down into
// UnitPriceSpecification components. Amounts collected at the property carry a
// PayAction whose recipient is the Hotel; everything else is paid to us
// (OnlineBusiness). See developers.google.com/hotels/hotel-prices/structured-data.

import { SITE } from "../lib/seo.js";

export const NAV = {
  landing: { "data-nav-stage": "landing" },
  finalStage: { "data-nav-stage": "ready-for-payment", "data-nav-stage-final": "true" },
  loading: { "data-nav-loading-indicator": "true" },
  click: order => ({ "data-nav-criticalpath": "true", "data-nav-interactiontype": "CLICK", "data-nav-interactionorder": String(order || 1) })
};

export const OFFER_TYPE = "https://schema.org/Offer https://schema.org/LodgingReservation";
export const COMPOUND_TYPE = "https://schema.org/CompoundPriceSpecification";
export const UNIT_TYPE = "https://schema.org/UnitPriceSpecification";

// "2026-09-20" + "04:00 PM" → "2026-09-20 16:00:00" (Google's DateTime shape).
export function stayDateTime(date, time, fallback = "15:00:00") {
  if (!date) return undefined;
  const m = String(time || "").trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!m) return `${date} ${fallback}`;
  let h = Number(m[1]); const min = m[2] || "00"; const ap = (m[3] || "").toUpperCase();
  if (ap === "PM" && h < 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return `${date} ${String(h).padStart(2, "0")}:${min}:00`;
}

export const money2 = n => (Math.round((Number(n) || 0) * 100) / 100).toFixed(2);

// Who collects this line: the online business (us) or the hotel at check-in.
export function PayRecipient({ atProperty, hotelName }) {
  return (
    <span itemProp="potentialAction" itemScope itemType="https://schema.org/PayAction" hidden>
      <span itemProp="recipient" itemScope itemType={atProperty ? "https://schema.org/Hotel" : "https://schema.org/OnlineBusiness"}>
        <meta itemProp="name" content={atProperty ? (hotelName || "The property") : SITE.name}/>
      </span>
    </span>
  );
}

// One UnitPriceSpecification component. Renders hidden metadata only; the
// visible amount, when there is one, sits next to it in the caller's markup.
export function PriceComponent({ line, currency = "USD", hotelName }) {
  return (
    <span itemProp="priceComponent" itemScope itemType={UNIT_TYPE} hidden>
      <meta itemProp="name" content={line.componentType || line.name}/>
      {line.componentType && <meta itemProp="priceComponentType" content={line.componentType}/>}
      <meta itemProp="price" content={money2(line.amount)}/>
      <meta itemProp="priceCurrency" content={currency}/>
      <PayRecipient atProperty={line.atProperty} hotelName={hotelName}/>
    </span>
  );
}

// Free cancellation → full refund until `cancelTime`; otherwise not permitted.
// Google assumes non-refundable when the policy is omitted, so always emit it.
export function ReturnPolicy({ refundable, cancelTime }) {
  return (
    <span itemProp="hasMerchantReturnPolicy" itemScope itemType="https://schema.org/MerchantReturnPolicy" hidden>
      {refundable ? (
        <>
          <meta itemProp="returnPolicyCategory" content="https://schema.org/MerchantReturnFiniteReturnWindow"/>
          <meta itemProp="refundType" content="https://schema.org/FullRefund"/>
          {cancelTime && <meta itemProp="merchantReturnDays" content={cancelTime}/>}
          <meta itemProp="restockingFee" content="0"/>
        </>
      ) : (
        <meta itemProp="returnPolicyCategory" content="https://schema.org/MerchantReturnNotPermitted"/>
      )}
    </span>
  );
}

// Sold-out itineraries still need an offer, with no priceSpecification.
export function SoldOutOffer({ checkin, checkout }) {
  return (
    <span itemProp="makesOffer" itemScope itemType={OFFER_TYPE} hidden>
      <meta itemProp="availability" content="https://schema.org/SoldOut"/>
      {checkin && <meta itemProp="checkinTime" content={stayDateTime(checkin)}/>}
      {checkout && <meta itemProp="checkoutTime" content={stayDateTime(checkout, null, "11:00:00")}/>}
    </span>
  );
}

// ISO cancel time from LiteAPI ("2026-09-18T23:59:00Z" style) → "YYYY-MM-DD HH:mm:ss".
export function cancelDateTime(v) {
  if (!v) return undefined;
  const m = String(v).match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(?::(\d{2}))?/);
  if (m) return `${m[1]} ${m[2]}:${m[3] || "00"}`;
  const d = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
  return d ? `${d[1]} 23:59:59` : undefined;
}
